import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import AccountAPI from '../../api/account';
import EnterpriseAccountAPI from '../../api/enterprise/account';
import { throwErrorMessage } from '../utils/api';

const findRecordById = ($state, id) =>
  $state.records.find(record => record.id === Number(id)) || {};

const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isUpdating: false,
    isCheckoutInProcess: false,
  },
  subscriptionInfo: {},
  subscriptionUiFlags: {
    isFetching: false,
  },
};

export const getters = {
  getAccount: $state => id => {
    return $state.records.find(record => record.id === Number(id)) || {};
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getSubscriptionInfo($state) {
    return $state.subscriptionInfo;
  },
  getSubscriptionUIFlags($state) {
    return $state.subscriptionUiFlags;
  },
  isFeatureEnabledonAccount:
    ($state, _, __, rootGetters) => (id, featureName) => {
      // If a user is SuperAdmin and has access to the account, then they would see all the available features
      const isUserASuperAdmin =
        rootGetters.getCurrentUser?.type === 'SuperAdmin';
      if (isUserASuperAdmin) {
        return true;
      }

      const { features = {} } = findRecordById($state, id);

      return features[featureName] || false;
    },
  // There are some features which can be enabled/disabled globally
  isFeatureEnabledGlobally: $state => (id, featureName) => {
    const { features = {} } = findRecordById($state, id);
    return features[featureName] || false;
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await AccountAPI.get();
      commit(types.default.ADD_ACCOUNT, response.data);
      commit(types.default.SET_ACCOUNT_UI_FLAG, {
        isFetchingItem: false,
      });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, {
        isFetchingItem: false,
      });
    }
  },
  update: async ({ commit }, updateObj) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: true });
    try {
      await AccountAPI.update('', updateObj);
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isUpdating: false });
      throw new Error(error);
    }
  },
  create: async ({ commit }, accountInfo) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: true });
    try {
      const response = await AccountAPI.createAccount(accountInfo);
      const account_id = response.data.data.account_id;
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: false });
      return account_id;
    } catch (error) {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCreating: false });
      throw error;
    }
  },

  checkout: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: true });
    try {
      const response = await EnterpriseAccountAPI.checkout();
      window.location = response.data.redirect_url;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  subscription: async ({ commit }) => {
    commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: true });
    try {
      await EnterpriseAccountAPI.subscription();
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.default.SET_ACCOUNT_UI_FLAG, { isCheckoutInProcess: false });
    }
  },

  limits: async ({ commit }) => {
    try {
      const response = await EnterpriseAccountAPI.getLimits();
      commit(types.default.SET_ACCOUNT_LIMITS, response.data);
    } catch (error) {
      // silent error
    }
  },

  fetchSubscriptionInfo: async ({ commit }) => {
    commit(types.default.SET_SUBSCRIPTION_UI_FLAG, { isFetching: true });
    try {
      const response = await AccountAPI.getSubscription();
      commit(types.default.SET_SUBSCRIPTION_INFO, response.data);
    } catch (error) {
      // silent error, page will show the empty state
    } finally {
      commit(types.default.SET_SUBSCRIPTION_UI_FLAG, { isFetching: false });
    }
  },
  cancelSubscription: async ({ commit, dispatch }) => {
    commit(types.default.SET_SUBSCRIPTION_UI_FLAG, { isCancelling: true });
    try {
      const response = await AccountAPI.cancelSubscription();
      await dispatch('accounts/fetchSubscriptionInfo', null, { root: true });
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.error || 'Something went wrong.',
      };
    } finally {
      commit(types.default.SET_SUBSCRIPTION_UI_FLAG, { isCancelling: false });
    }
  },
  resumeSubscription: async ({ commit, dispatch }) => {
    commit(types.default.SET_SUBSCRIPTION_UI_FLAG, { isResuming: true });
    try {
      const response = await AccountAPI.resumeSubscription();
      await dispatch('accounts/fetchSubscriptionInfo', null, { root: true });
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.error || 'Something went wrong.',
      };
    } finally {
      commit(types.default.SET_SUBSCRIPTION_UI_FLAG, { isResuming: false });
    }
  },
};

export const mutations = {
  [types.default.SET_ACCOUNT_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.ADD_ACCOUNT]: MutationHelpers.setSingleRecord,
  [types.default.EDIT_ACCOUNT]: MutationHelpers.update,
  [types.default.SET_ACCOUNT_LIMITS]: MutationHelpers.updateAttributes,
  [types.default.SET_SUBSCRIPTION_INFO]($state, data) {
    $state.subscriptionInfo = data;
  },
  [types.default.SET_SUBSCRIPTION_UI_FLAG]($state, data) {
    $state.subscriptionUiFlags = {
      ...$state.subscriptionUiFlags,
      ...data,
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
